import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  InputBase,
  Box,
  Tooltip,
  Button,
  Collapse,
} from '@mui/material';
import { Search, Close, FileDownload, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { fetchUserCancellationReports } from '../../../actions/userCancellationActions';
import { exportCancellationDataCSV } from '../../../actions/exportCSVActions';
import UserCancellationTable from './UserCancellationTable';
import CancellationDistributionChart from './CancellationDistributionChart';
import CancellationKpiCards from './CancellationKpiCards';
import CancellationOverTimeChart from './CancellationOverTimeChart';
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import { DEFAULT_PER_PAGE, PARSE_INT_RADIX } from '../../../constants/pagination';

const UserCancellationPage = () => {
  const dispatch = useDispatch();
  const searchInputRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // Raw event table is now secondary — chart is the primary view. Operators
  // who want to look up a specific user / cancellation can expand it.
  const [rawEventsOpen, setRawEventsOpen] = useState(false);
  const { user_cancellation_reports, pagination } = useSelector(state => state.user_cancellations);
  // KPI cards live above the chart but render from the same /distribution
  // response the chart fetches. Pull the slice directly so the cards don't
  // need the chart to thread the data down as a prop.
  const distributionData = useSelector(state => state.user_cancellations.distribution.data);
  const distributionLoading = useSelector(state => state.user_cancellations.distribution.loading);

  // Period selector lives at the page level so both the chart and the raw
  // events drill-down can stay in lockstep with whatever the operator
  // picked. The chart's selector UI is controlled — its onChange flows back
  // up to here. Defaults match what the chart used pre-lift.
  const [period, setPeriod] = useState("last_30_days");
  const [year, setYear] = useState(new Date().getFullYear() - 1);

  // Build the filter the API expects; year is only sent when relevant.
  const indexFilters = useMemo(
    () => ({ period, year: period === "year" ? year : undefined }),
    [period, year]
  );

  // Lazy-load the raw events list — only fetch when the drill-down is open
  // AND the (period, year) filter combination hasn't already been fetched.
  // Comparing serialized filters via a ref both avoids the wasted refetch
  // when the operator just toggles the drawer back open AND correctly
  // distinguishes "never fetched" from "fetched zero rows" (a length-based
  // guard would conflate the two for genuinely empty windows).
  const lastFiltersFetched = useRef(null);
  useEffect(() => {
    if (!rawEventsOpen) return;
    const key = JSON.stringify(indexFilters);
    if (lastFiltersFetched.current === key) return;
    lastFiltersFetched.current = key;
    dispatch(fetchUserCancellationReports(1, DEFAULT_PER_PAGE, indexFilters));
  }, [rawEventsOpen, dispatch, indexFilters]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f' && e.shiftKey) {
        e.preventDefault();
        // Search only makes sense over the raw events list — auto-open it.
        if (!rawEventsOpen) setRawEventsOpen(true);
        if (!isSearchOpen) handleToggleSearch();
      }
      if (e.key === 'Escape' && isSearchOpen) {
        e.preventDefault();
        handleToggleSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, rawEventsOpen]);

  const handleSearchChange = (e) => setSearchText(e.target.value);

  const handleToggleSearch = () => {
    setIsSearchOpen((prev) => !prev);
    if (isSearchOpen) {
      setSearchText("");
    } else {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  };

  const handleExport = () => dispatch(exportCancellationDataCSV());

  const handlePageChange = (event, newPage) => {
    dispatch(fetchUserCancellationReports(newPage + 1, pagination.page_size, indexFilters));
  };

  const handleRowsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, PARSE_INT_RADIX);
    dispatch(fetchUserCancellationReports(1, newPerPage, indexFilters));
  };

  return (
    <MainContentContainer>
      <AppBar position="sticky" sx={{ width: '100%' }}>
        <Toolbar>
          {isSearchOpen ? (
            <Box display="flex" alignItems="center" flexGrow={1}>
              <InputBase
                inputRef={searchInputRef}
                value={searchText}
                onChange={handleSearchChange}
                sx={{
                  flexGrow: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: "white",
                  borderRadius: 1,
                  '& input': { color: 'text.primary' }
                }}
                placeholder="Search raw events..."
              />
              <IconButton onClick={handleToggleSearch} color="inherit">
                <Close />
              </IconButton>
            </Box>
          ) : (
            <>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                User Cancellation Report
              </Typography>
              {rawEventsOpen && (
                // Search only meaningful when the raw events table is visible.
                <Tooltip title="Search raw events (⌘⇧F)">
                  <IconButton onClick={handleToggleSearch} color="inherit">
                    <Search />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Export to CSV">
                <IconButton onClick={handleExport} color="inherit">
                  <FileDownload />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ width: '100%', mt: 2 }}>
        <CancellationKpiCards data={distributionData} loading={distributionLoading} />

        <CancellationDistributionChart
          period={period}
          year={year}
          onPeriodChange={setPeriod}
          onYearChange={setYear}
        />

        <CancellationOverTimeChart />

        <Button
          startIcon={rawEventsOpen ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          onClick={() => setRawEventsOpen((open) => !open)}
          sx={{ mb: 1 }}
        >
          {rawEventsOpen ? "Hide raw events" : "Show raw events"}
        </Button>

        <Collapse in={rawEventsOpen} unmountOnExit>
          <UserCancellationTable
            data={user_cancellation_reports}
            searchText={searchText}
            pagination={pagination}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Collapse>
      </Box>
    </MainContentContainer>
  );
};

export default UserCancellationPage;
